import { OpusAgentModel } from "@src/types.generated";

// All per-chat model bookkeeping for the Opus Agent drawer, as one plain
// structure plus pure transitions. The hook holds a single instance and
// derives the picker's state from it; every ordering rule lives here, where
// it is unit-testable in isolation:
//
// - pending picks are per chat, each stamped with a pick sequence, and
//   consumed by IDENTITY: a send's success clears the pending entry only if
//   it is the very pick that send carried (same pickSeq). A newer pick made
//   mid-flight survives — including a re-pick of the same value, which a
//   value comparison could not distinguish.
// - stored writes are MONOTONIC IN SUBMIT ORDER: each model-carrying send
//   takes a submission sequence, and a success writes the stored model only
//   if no later-submitted send has already written. The server's final state
//   is send order, not resolve order — and a FAILED send simply never
//   writes, so it can neither win nor block an earlier success. (No failure
//   transition exists: failure changes nothing here, including pending.)
// - adopting a created chat migrates the unsaved-chat entries (pending,
//   stored, writtenSeq) to the real id.
// - a pick made before the initial most-recent-chat lookup resolves lives
//   under UNRESOLVED_CHAT_KEY and migrates to whatever chat becomes current.
// - hydration reads are version-guarded: a fetch that started before a
//   send-success wrote the stored model may not roll it back.

export const NEW_CHAT_KEY = "new";
export const UNRESOLVED_CHAT_KEY = "unresolved";

export type PendingPick = {
  model: OpusAgentModel;
  // Identity of the pick event, from the shared monotonic counter.
  pickSeq: number;
};

export type ChatModelState = {
  // The chat's stored (server) selection as this client knows it. A key's
  // PRESENCE means "known"; null means "the server default".
  stored: Record<string, OpusAgentModel | null>;
  // Picks not yet carried by a send.
  pending: Record<string, PendingPick>;
  // Per chat: the highest submission sequence whose success wrote `stored`.
  writtenSeq: Record<string, number>;
  // Monotonic counter shared by pick events and model-carrying submissions.
  seq: number;
  // Bumped on every stored write from a send-success; hydration fetches that
  // started before the current version may not overwrite.
  version: number;
  // The composer's current bookkeeping key, so a resolution can tell an
  // abandoned unsaved composition from one being re-resolved in place.
  currentKey: string;
};

export const initialChatModelState = (): ChatModelState => ({
  stored: {},
  pending: {},
  writtenSeq: {},
  seq: 0,
  version: 0,
  currentKey: UNRESOLVED_CHAT_KEY,
});

// The bookkeeping key for the current composer target.
export const chatKeyFor = (
  chatId: string | null,
  selectedChatId: string | null | undefined,
): string =>
  chatId ?? (selectedChatId === undefined ? UNRESOLVED_CHAT_KEY : NEW_CHAT_KEY);

export const selectModel = (
  state: ChatModelState,
  key: string,
  model: OpusAgentModel,
): ChatModelState => {
  const pickSeq = state.seq + 1;
  return {
    ...state,
    seq: pickSeq,
    pending: { ...state.pending, [key]: { model, pickSeq } },
  };
};

// A chat became current (initial lookup resolved, an explicit switch, or a
// new-chat composition began): any pick made while unresolved follows it,
// and the cache's knowledge of its stored model (undefined = unknown) seeds
// `stored` without erasing prior knowledge.
//
// An unsaved composition's pick is scoped to that composition: resolving
// AWAY from it to another chat abandons the pick (the device preference
// already holds it if it was the user's last explicit pick, so nothing the
// user chose is lost). Starting a fresh composition seeds strictly from
// `newChatPreferredModel` (the sticky last pick) as a normal, consumable
// pick — unless an unresolved pick just migrated in, which wins. Re-resolving
// the composition you are already in is a no-op for picks, never a reseed.
// Existing chats never see the preference: their stored (server) model wins.
export const chatResolved = (
  state: ChatModelState,
  key: string,
  seed: OpusAgentModel | null | undefined,
  newChatPreferredModel: OpusAgentModel | null = null,
): ChatModelState => {
  const pending = { ...state.pending };
  let seq = state.seq;
  const wasUnsaved = state.currentKey === NEW_CHAT_KEY;
  if (wasUnsaved && key !== NEW_CHAT_KEY) delete pending[NEW_CHAT_KEY];
  if (key !== UNRESOLVED_CHAT_KEY && UNRESOLVED_CHAT_KEY in pending) {
    pending[key] = pending[UNRESOLVED_CHAT_KEY];
    delete pending[UNRESOLVED_CHAT_KEY];
  }
  const startingFreshComposition = key === NEW_CHAT_KEY && !wasUnsaved;
  if (startingFreshComposition && newChatPreferredModel && !(key in pending)) {
    seq += 1;
    pending[key] = { model: newChatPreferredModel, pickSeq: seq };
  }
  const stored =
    seed !== undefined ? { ...state.stored, [key]: seed } : state.stored;
  return { ...state, pending, stored, seq, currentKey: key };
};

// A send is going out for `key`: it carries the chat's pending pick, if any,
// taking a submission sequence and snapshotting WHICH pick it carries.
export const sendSubmitted = (
  state: ChatModelState,
  key: string,
): {
  state: ChatModelState;
  model: OpusAgentModel | undefined;
  seq: number;
  pickSeq: number;
} => {
  const pick = state.pending[key];
  if (!pick) return { state, model: undefined, seq: 0, pickSeq: 0 };
  const seq = state.seq + 1;
  return {
    state: { ...state, seq },
    model: pick.model,
    seq,
    pickSeq: pick.pickSeq,
  };
};

// A send succeeded. Returns the stored write to mirror into the Apollo cache
// (so a later reseed agrees with the server), or null when nothing was
// written (no model carried, or a later-submitted send already wrote).
export const sendSucceeded = (
  state: ChatModelState,
  key: string,
  model: OpusAgentModel | undefined,
  seq: number,
  pickSeq: number,
  adoptedChatId: string | null,
): {
  state: ChatModelState;
  storedWrite: { chatId: string; model: OpusAgentModel } | null;
} => {
  const pending = { ...state.pending };
  const stored = { ...state.stored };
  const writtenSeq = { ...state.writtenSeq };
  // Consume by IDENTITY: only the very pick this send carried. A newer pick
  // — even a re-pick of the same value — has a different pickSeq and stays.
  if (model && pending[key]?.pickSeq === pickSeq) delete pending[key];
  // Adopt: the unsaved chat's entries follow the created chat's id.
  if (adoptedChatId && key === NEW_CHAT_KEY) {
    if (NEW_CHAT_KEY in pending) {
      pending[adoptedChatId] = pending[NEW_CHAT_KEY];
      delete pending[NEW_CHAT_KEY];
    }
    if (NEW_CHAT_KEY in stored && !(adoptedChatId in stored)) {
      stored[adoptedChatId] = stored[NEW_CHAT_KEY];
    }
    delete stored[NEW_CHAT_KEY];
    if (NEW_CHAT_KEY in writtenSeq) {
      writtenSeq[adoptedChatId] = Math.max(
        writtenSeq[adoptedChatId] ?? 0,
        writtenSeq[NEW_CHAT_KEY],
      );
      delete writtenSeq[NEW_CHAT_KEY];
    }
  }
  const effectiveKey = adoptedChatId ?? key;
  // The composer now lives in the adopted chat, not the unsaved composition.
  const currentKey =
    adoptedChatId && state.currentKey === NEW_CHAT_KEY
      ? adoptedChatId
      : state.currentKey;
  let version = state.version;
  let storedWrite: { chatId: string; model: OpusAgentModel } | null = null;
  if (model && seq > (writtenSeq[effectiveKey] ?? 0)) {
    writtenSeq[effectiveKey] = seq;
    stored[effectiveKey] = model;
    version += 1;
    storedWrite = { chatId: effectiveKey, model };
  }
  return {
    state: { ...state, pending, stored, writtenSeq, version, currentKey },
    storedWrite,
  };
};

// A hydration / re-hydration fetch delivered the chat's stored model.
// Applies only when no send-success wrote since the fetch STARTED. Returns
// whether it applied (a no-cache re-hydration mirrors into the Apollo cache
// on apply; the normal hydration query writes the cache natively).
export const hydrated = (
  state: ChatModelState,
  key: string,
  model: OpusAgentModel | null,
  startedVersion: number,
): { state: ChatModelState; applied: boolean } => {
  if (startedVersion !== state.version) return { state, applied: false };
  return {
    state: { ...state, stored: { ...state.stored, [key]: model } },
    applied: true,
  };
};

// What the NEXT turn runs on: the pending pick, else the known stored model
// (null = the server default), else — for a chat whose stored model is
// genuinely unknown — null, the picker's indeterminate state (never a
// default the chat may not run on).
export const effectiveChatModel = (
  state: ChatModelState,
  key: string,
  defaultModel: OpusAgentModel | null,
): OpusAgentModel | null => {
  const pending = state.pending[key];
  if (pending) return pending.model;
  if (key in state.stored) return state.stored[key] ?? defaultModel;
  return null;
};
